import { FC, useCallback, useEffect, useRef } from 'react';

import {
  AdElementText,
  AdTemplate,
  CountdownFormatStyle,
  CountdownTimeGranularity,
  DateTimeFormat,
} from '@adflow/types';
import { Box, FormLabel, Heading, Select, SimpleGrid, Text, VStack } from '@chakra-ui/react';

import { useStore } from '../../../../hooks/useStore';

type Props = {
  textElement: AdElementText;
  importSourceType?: AdTemplate['importSourceType'];
};

export const isCountdownPresent = (textElement: AdElementText) => {
  if (!textElement.sources?.length) return false;
  return textElement.sources.some(
    (s) =>
      textElement.data.content.includes(`${s.sourceId}:COUNTDOWN}`) ||
      textElement.data.content.includes(`${s.sourceId}:PRE_COUNTDOWN}`),
  );
};

type FormatStyleOption = {
  value: CountdownFormatStyle;
  label: string;
  example: string;
};

const formatStyleOptions: FormatStyleOption[] = [
  { value: 'digital', label: 'Digital', example: '04:12:09' },
  { value: 'words-long', label: 'Words - Long', example: '3 Days 4 Hours 12 Minutes' },
  { value: 'words-short', label: 'Words - Short', example: '3D 4H 12M' },
  { value: 'days-digital', label: 'Days words, other digital', example: '3 Days 04:12:09' },
];

// Video templates: only Words formats (Digital is confusing with D_H like "0:13")
const videoFormatStyleOptions: FormatStyleOption[] = [
  { value: 'words-long', label: 'Words - Long', example: '2 Days 4 Hours' },
  { value: 'words-short', label: 'Words - Short', example: '2D 4H' },
];

type GranularityOption = {
  value: CountdownTimeGranularity;
  label: string;
};

const allGranularityOptions: GranularityOption[] = [
  { value: 'D_H_M_S', label: 'Days, Hours, Min, Sec' },
  { value: 'H_M_S', label: 'Hours, Min, Sec' },
  { value: 'D_H_M', label: 'Days, Hours, Min' },
  { value: 'D_H', label: 'Days, Hours' },
  { value: 'D', label: 'Days' },
  { value: 'H', label: 'Hours' },
];

// Video templates only support coarse granularity (no minutes/seconds)
const videoGranularityOptions: GranularityOption[] = [
  { value: 'D_H', label: 'Days, Hours' },
  { value: 'D', label: 'Days' },
];

// Valid granularity values for video templates
const validVideoGranularities: CountdownTimeGranularity[] = ['D_H', 'D'];

// Valid format styles for video templates (only Words formats - Digital is confusing)
const validVideoFormatStyles: CountdownFormatStyle[] = ['words-long', 'words-short'];

// Valid granularities per format style for HTML templates.
// Clock-like positional formats stay restricted: D_H_M → "5:30" looks like clock time.
// Digital + Days and Digital + Hours are bare numbers on purpose; the unit lives in a
// separate static text element in the design.
const htmlGranularitiesByFormat: Record<CountdownFormatStyle, CountdownTimeGranularity[]> = {
  digital: ['D_H_M_S', 'H_M_S', 'D', 'H'],
  'words-long': ['D_H_M_S', 'H_M_S', 'D_H_M', 'D_H', 'D'],
  'words-short': ['D_H_M_S', 'H_M_S', 'D_H_M', 'D_H', 'D'],
  'days-digital': ['D_H_M_S', 'H_M_S', 'D'],
};

/**
 * Get valid granularity options for a given format style and template type.
 */
export function getGranularityOptionsForFormat(
  formatStyle: CountdownFormatStyle,
  isVideoTemplate: boolean,
): GranularityOption[] {
  if (isVideoTemplate) return videoGranularityOptions;
  const validValues =
    htmlGranularitiesByFormat[formatStyle] ?? allGranularityOptions.map((o) => o.value);
  return allGranularityOptions.filter((o) => validValues.includes(o.value));
}

/**
 * Get a valid granularity for the template type and format style.
 * Video templates can only use D_H or D (no minutes/seconds).
 * HTML formats restrict granularities that would look like a clock.
 * Hours-only (H) is valid for Digital HTML only.
 */
export const getValidGranularity = (
  granularity: CountdownTimeGranularity | null | undefined,
  isVideoTemplate: boolean,
  formatStyle?: CountdownFormatStyle | null,
): CountdownTimeGranularity => {
  const defaultGranularity = isVideoTemplate ? 'D_H' : 'H_M_S';

  if (!granularity) return defaultGranularity;

  // For video templates, ensure granularity is valid (no minutes/seconds)
  if (isVideoTemplate && !validVideoGranularities.includes(granularity)) {
    return 'D_H'; // Default to D_H for invalid video granularities
  }

  // For HTML templates, validate against format-specific restrictions
  if (!isVideoTemplate && formatStyle) {
    const validGranularities = htmlGranularitiesByFormat[formatStyle];
    if (validGranularities && !validGranularities.includes(granularity)) {
      return defaultGranularity;
    }
  }

  return granularity;
};

/**
 * Get a valid format style for the template type.
 * Video templates can only use 'words-long' or 'words-short'.
 */
export const getValidFormatStyle = (
  formatStyle: CountdownFormatStyle | null | undefined,
  isVideoTemplate: boolean,
): CountdownFormatStyle => {
  const defaultFormatStyle = isVideoTemplate ? 'words-long' : 'digital';

  if (!formatStyle) return defaultFormatStyle;

  // For video templates, only 'words-long' and 'words-short' are valid
  if (isVideoTemplate && !validVideoFormatStyles.includes(formatStyle)) {
    return 'words-long';
  }

  return formatStyle;
};

/**
 * Build countdown options for both element-level and template-level dateTimeFormat updates.
 */
export const buildCountdownOptions = (
  dateDisplayOptions: DateTimeFormat | null | undefined,
  countdownFormatStyle: CountdownFormatStyle,
  countdownTimeGranularity: CountdownTimeGranularity,
): DateTimeFormat => {
  return {
    ...dateDisplayOptions,
    countdownFormatStyle,
    countdownTimeGranularity,
  };
};

const CountdownFormatControls: FC<Props> = ({ textElement, importSourceType }) => {
  const s = useStore((state) => ({
    displayOptions: state.displayOptions,
    setElementDateDisplayOptions: state.setElementDateDisplayOptions,
    setDisplayOptions: state.setDisplayOptions,
  }));

  const dateDisplayOptions: DateTimeFormat | null | undefined =
    textElement.data.dateDisplayOptions || s.displayOptions.dateTimeFormat;

  const isVideoTemplate = importSourceType === 'creatomate';
  const formatOptions = isVideoTemplate ? videoFormatStyleOptions : formatStyleOptions;

  // Track which element has been corrected to prevent infinite loops
  // Reset when element changes
  const correctedElementId = useRef<string | null>(null);

  // Auto-correct invalid granularities and format styles for video templates,
  // and initialize countdown fields when missing (e.g., element had {DATE} replaced with {COUNTDOWN})
  useEffect(() => {
    // Skip if we've already corrected this specific element
    if (correctedElementId.current === textElement.id) return;

    // Check if video template has invalid granularity (e.g., H_M_S)
    const currentGranularity = dateDisplayOptions?.countdownTimeGranularity;
    const needsGranularityCorrection =
      isVideoTemplate &&
      currentGranularity &&
      !validVideoGranularities.includes(currentGranularity);

    // Check if video template has invalid format style (e.g., days-digital)
    const currentFormatStyle = dateDisplayOptions?.countdownFormatStyle;
    const needsFormatStyleCorrection =
      isVideoTemplate && currentFormatStyle && !validVideoFormatStyles.includes(currentFormatStyle);

    // Check if countdown fields are missing entirely (e.g., element had {DATE} replaced with {COUNTDOWN})
    // Without this, the UI shows validated defaults but never persists them,
    // so template-level displayOptions (possibly invalid for video) fill in at render time
    const needsCountdownFieldsInit = !currentFormatStyle || !currentGranularity;

    if (needsGranularityCorrection || needsFormatStyleCorrection || needsCountdownFieldsInit) {
      correctedElementId.current = textElement.id;

      const resolvedFormatStyle =
        currentFormatStyle || (isVideoTemplate ? 'words-long' : 'digital');
      const correctedFormatStyle =
        isVideoTemplate && !validVideoFormatStyles.includes(resolvedFormatStyle)
          ? 'words-long'
          : resolvedFormatStyle;

      const correctedGranularity = needsGranularityCorrection
        ? 'D_H'
        : getValidGranularity(currentGranularity, isVideoTemplate, correctedFormatStyle);

      const updatedOptions = buildCountdownOptions(
        dateDisplayOptions,
        correctedFormatStyle,
        correctedGranularity,
      );

      s.setElementDateDisplayOptions(textElement.id, updatedOptions);
      s.setDisplayOptions(
        'dateTimeFormat',
        buildCountdownOptions(
          s.displayOptions.dateTimeFormat,
          correctedFormatStyle,
          correctedGranularity,
        ),
      );
    } else {
      // Mark as "checked" even if no correction needed
      correctedElementId.current = textElement.id;
    }
  }, [dateDisplayOptions, isVideoTemplate, s, textElement.id]);

  // Current values with defaults - validate for template type
  const currentFormatStyle: CountdownFormatStyle = getValidFormatStyle(
    dateDisplayOptions?.countdownFormatStyle,
    isVideoTemplate,
  );
  const currentGranularity: CountdownTimeGranularity = getValidGranularity(
    dateDisplayOptions?.countdownTimeGranularity,
    isVideoTemplate,
    currentFormatStyle,
  );

  // Granularity options filtered by current format style
  const granularityOptions = getGranularityOptionsForFormat(currentFormatStyle, isVideoTemplate);

  const handleUpdateFormatStyle = useCallback(
    (value: CountdownFormatStyle) => {
      // Validate both format style and granularity for template type
      const validatedFormatStyle = getValidFormatStyle(value, isVideoTemplate);
      const granularity = getValidGranularity(
        dateDisplayOptions?.countdownTimeGranularity,
        isVideoTemplate,
        validatedFormatStyle,
      );
      const updatedOptions = buildCountdownOptions(
        dateDisplayOptions,
        validatedFormatStyle,
        granularity,
      );
      s.setElementDateDisplayOptions(textElement.id, updatedOptions);
      // Also update template-level displayOptions
      s.setDisplayOptions(
        'dateTimeFormat',
        buildCountdownOptions(s.displayOptions.dateTimeFormat, validatedFormatStyle, granularity),
      );
    },
    [s, dateDisplayOptions, textElement.id, isVideoTemplate],
  );

  const handleUpdateGranularity = useCallback(
    (value: CountdownTimeGranularity) => {
      const formatStyle = getValidFormatStyle(
        dateDisplayOptions?.countdownFormatStyle,
        isVideoTemplate,
      );
      // Validate the new granularity for template type and format style
      const validatedGranularity = getValidGranularity(value, isVideoTemplate, formatStyle);
      const updatedOptions = {
        ...dateDisplayOptions,
        countdownTimeGranularity: validatedGranularity,
        countdownFormatStyle: formatStyle,
      };
      s.setElementDateDisplayOptions(textElement.id, updatedOptions);
      // Also update template-level displayOptions
      s.setDisplayOptions(
        'dateTimeFormat',
        buildCountdownOptions(s.displayOptions.dateTimeFormat, formatStyle, validatedGranularity),
      );
    },
    [s, dateDisplayOptions, textElement.id, isVideoTemplate],
  );

  return (
    <VStack align="stretch" spacing={4} mt={2}>
      <Heading fontSize="lg" fontWeight="semibold" noOfLines={1}>
        Countdown Settings
      </Heading>

      {/* Format Style Section */}
      <Box>
        <FormLabel fontWeight="semibold" mb={2}>
          Format Style
        </FormLabel>
        <SimpleGrid columns={2} spacing={2}>
          {formatOptions.map((option) => (
            <Box
              key={option.value}
              as="button"
              type="button"
              onClick={() => handleUpdateFormatStyle(option.value)}
              p={3}
              borderWidth="1px"
              borderRadius="md"
              borderColor={currentFormatStyle === option.value ? 'blue.500' : 'gray.200'}
              bg={currentFormatStyle === option.value ? 'blue.50' : 'white'}
              _hover={{ borderColor: 'blue.300' }}
              textAlign="left"
              transition="all 0.2s"
            >
              <Text fontSize="sm" fontWeight="medium" color="gray.800">
                {option.example}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {option.label}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* Time Granularity Section */}
      <Box>
        <FormLabel fontWeight="semibold" mb={2}>
          Time Granularity
        </FormLabel>
        <Select
          value={currentGranularity}
          onChange={(e) => handleUpdateGranularity(e.target.value as CountdownTimeGranularity)}
          data-test-id="countdown-granularity-select"
        >
          {granularityOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Box>
    </VStack>
  );
};

CountdownFormatControls.displayName = 'CountdownFormatControls';

export default CountdownFormatControls;
